import { type ComponentProps, memo, useMemo } from 'react';

import { CircleSlashIcon } from '@air/design-icons';
import { getPercentage } from '@air/utilities';
import { cn, tailwindVariants } from '@air/utils-tailwind';

import { type CreditUsageStats } from '~/components/CreditUsageMeter/types';
import { formatCreditUsage, getCreditUsageDisplayState } from '~/components/CreditUsageMeter/utils';
import { useAnimatedNumber } from '~/hooks/useAnimatedNumber';

const creditUsageMeterCountVariants = tailwindVariants({
  slots: {
    counts: 'tabular-nums',
    remaining: 'rounded-4 py-0.5 font-semibold ads:text-primary text-gray-12',
    separator: 'ads:text-metadata text-gray-10',
    allocated: 'ads:text-metadata text-gray-10',
  },
  variants: {
    size: {
      default: {
        counts: 'text-14',
      },
      compact: {
        counts: 'text-12',
        separator: 'mr-1 ml-0.5 ads:text-metadata text-gray-8',
      },
    },
    usageState: {
      default: {
        remaining: '',
      },
      '95%': {
        remaining: 'ads:bg-error bg-flash-9 px-1 text-white',
      },
      depleted: {
        remaining: 'ads:bg-error bg-flash-9 px-1 text-white',
      },
    },
  },
});

export type CreditUsageMeterCountProps = Pick<ComponentProps<'div'>, 'className'> & {
  size?: 'default' | 'compact';
  /** When provided and > 0, folds gift credits into the displayed total. */
  giftCreditsBalance?: number;
  /** Defaults to true. Set false when the gift is itemized elsewhere (e.g. the nav popover legend). */
  showGiftIndicator?: boolean;
  allowCreditOverage: boolean;
  /** Appends the share of the total already used, e.g. "(12%)". */
  showPercentage?: boolean;
} & Pick<CreditUsageStats, 'allocated' | 'used'>;

export const CreditUsageMeterCount = memo(
  ({
    allocated,
    used,
    size = 'default',
    className,
    giftCreditsBalance = 0,
    showGiftIndicator = true,
    allowCreditOverage,
    showPercentage = false,
  }: CreditUsageMeterCountProps) => {
    const showGiftCredits = showGiftIndicator && giftCreditsBalance > 0;
    // Gift credits are part of the spendable total, so fold them into the "of N credits" denominator.
    const totalAllocated = (allocated ?? 0) + giftCreditsBalance;

    // Derive the usage state from the same total the count and bar use, so the warning styling
    // doesn't say "depleted" while the count still shows credits remaining.
    const usageState = useMemo(
      () =>
        getCreditUsageDisplayState({
          allowCreditOverage,
          used,
          totalCredits: totalAllocated,
        }),
      [allowCreditOverage, totalAllocated, used],
    );

    const percentUsed = Math.round(getPercentage(used.total, totalAllocated));

    const animatedUsedCount = useAnimatedNumber(used.total);
    const animatedAllocatedCount = useAnimatedNumber(totalAllocated);

    const { leftSide, separator, rightSide } = useMemo(() => {
      const rightSide = formatCreditUsage(animatedAllocatedCount ?? totalAllocated);

      if (size !== 'compact') {
        return {
          leftSide: formatCreditUsage(animatedUsedCount ?? used.total),
          separator: ' used of ',
          rightSide: `${rightSide} credits`,
        };
      }

      return {
        leftSide: formatCreditUsage(animatedUsedCount ?? used.total),
        separator: '/',
        rightSide,
      };
    }, [animatedAllocatedCount, animatedUsedCount, size, totalAllocated, used.total]);

    if (!allocated) {
      return null;
    }

    return (
      <div className={cn('flex items-center gap-1', className)} data-testid="CREDIT_USAGE_METER_COUNT">
        {size === 'compact' && <CircleSlashIcon className="ads:text-icon-emphasis text-gray-12" size="sm" />}
        <div className={creditUsageMeterCountVariants({ size, usageState }).counts()}>
          <span className={creditUsageMeterCountVariants({ size, usageState }).remaining()}>{leftSide}</span>
          {/* biome-ignore lint/suspicious/noUnnecessaryConditions: separator is optional string; empty string should not render */}
          {separator && (
            <span className={creditUsageMeterCountVariants({ size, usageState }).separator()}>{separator}</span>
          )}
          {rightSide && (
            <span className={creditUsageMeterCountVariants({ size, usageState }).allocated()}>{rightSide}</span>
          )}
        </div>
        {showPercentage && size !== 'compact' && (
          <span
            className="ads:text-metadata text-14 text-gray-10 tabular-nums"
            data-testid="CREDIT_USAGE_METER_PERCENTAGE"
          >
            ({percentUsed}%)
          </span>
        )}
        {showGiftCredits && (
          <span
            data-testid="CREDIT_USAGE_METER_GIFT_DOT"
            title="Includes one-time gift credits"
            className="size-1.5 rounded-full ads:bg-ads-altitude-9 bg-blue-9"
          />
        )}
      </div>
    );
  },
);

CreditUsageMeterCount.displayName = 'CreditUsageMeterCount';
